
import {defineComponent, nextTick, PropType} from "vue";
import {Campaign, CollectUserData} from "@/models/campaign";
import {marked} from "marked";
import {Helper} from "@/utils/helper";
import {mapActions} from "pinia";
import {AnalyticsAction, useAnalyticsStore} from "@/store/analytics.store";

export interface OnDetailsCollectedPayload {
    name?: string;
    email?: string;
    termsConfirmed?: boolean;
    privacyConfirmed?: boolean;
}

interface Data {
    agentMessage: string;
    name: string;
    email: string;
    termsConfirmed: boolean;
    privacyConfirmed: boolean;
    nameStepComplete: boolean;
    emailStepComplete: boolean;
    termsStepComplete: boolean;
}

export default defineComponent({
    props: {
        campaign: {
            type: Object as PropType<Campaign>,
            required: true
        },
        isPreview: {
            type: Boolean,
            required: true
        },
    },
    data(): Data {
        return {
            agentMessage: "",
            name: "",
            email: "",
            termsConfirmed: false,
            privacyConfirmed: false,
            nameStepComplete: false,
            emailStepComplete: false,
            termsStepComplete: false,
        };
    },
    mounted() {
        this.updatedAgentMessage();
    },
    watch: {
        campaign: {
            handler(): void {
                this.updatedAgentMessageForce();
            },
            deep: true,
        }
    },
    computed: {
        showField() {
            if (this.campaign.collectNames != CollectUserData.Disabled && !this.nameStepComplete) {
                return "name";
            }

            if (this.campaign.collectEmails != CollectUserData.Disabled && !this.emailStepComplete) {
                return "email";
            }

            if ((this.campaign.enforcesTermsAndConditionsConsent || this.campaign.enforcesPrivacyPolicyConsent) && !this.termsStepComplete) {
                return "terms";
            }

            return "finished"
        },
        showNextButton(): boolean {
            if (this.showField === "name") {
                return this.campaign.collectNames == CollectUserData.Optional || !!this.name;
            } else if (this.showField === "email") {
                return this.campaign.collectEmails == CollectUserData.Optional || Helper.isValidEmail(this.email);
            } else {
                return (!this.campaign.enforcesTermsAndConditionsConsent || this.termsConfirmed) && (!this.campaign.enforcesPrivacyPolicyConsent || this.privacyConfirmed);
            }
        },
        getTermsUrl(): string {
            if (this.campaign.termsURL) {
                return this.campaign.termsURL;
            }
            return `${this.campaign.contributionURL}?legal=terms`;
        },

        getPrivacyUrl(): string {
            if (this.campaign.privacyURL) {
                return this.campaign.privacyURL;
            }
            return `${this.campaign.contributionURL}?legal=privacy`;
        },

        termsAndConditionsConsentText(): string {
            const originalTermsAndConditionsConsentText = this.campaign.termsAndConditionsConsentText ? this.campaign.termsAndConditionsConsentText : this.$t('survey.transferOfRightsLabel');
            const termsAndConditionsConsentTextWithLink = originalTermsAndConditionsConsentText.replaceAll('##TERMS_LINK##', this.getTermsUrl).replaceAll('##PRIVACY_POLICY_LINK##', this.getPrivacyUrl);

            const markdown = marked.parse(termsAndConditionsConsentTextWithLink, {
                gfm: true,
                breaks: true,
            });
            return markdown.replaceAll('<a', '<a class="link" target="_blank"');
        },

        privacyPolicyText(): string {
            const originalPrivacyPolicyConsentText = this.campaign.privacyPolicyConsentText ?? "";
            const privacyPolicyTextWithLink = originalPrivacyPolicyConsentText.replaceAll('##TERMS_LINK##', this.getTermsUrl).replaceAll('##PRIVACY_POLICY_LINK##', this.getPrivacyUrl);

            const markdown = marked.parse(privacyPolicyTextWithLink, {
                gfm: true,
                breaks: true,
            });
            return markdown.replaceAll('<a', '<a class="link" target="_blank"');
        },
    },
    methods: {
        // analytics store
        ...mapActions(useAnalyticsStore, ['trackPageView']),
        ...mapActions(useAnalyticsStore, ['trackEvent']),
        ...mapActions(useAnalyticsStore, ['sendAnalyticsEvent']),

        next() {
            if (this.showField === "name") {
                this.nameStepComplete = true;
                this.trackEvent('creationDetailsNameComplete', {
                    ...this.$analytics.campaignParams(this.campaign),
                });
                this.sendAnalyticsEvent(AnalyticsAction.NameSubmitted, this.campaign.id);
            } else if (this.showField === "email") {
                this.emailStepComplete = true;
                this.trackEvent('creationDetailsEmailComplete', {
                    ...this.$analytics.campaignParams(this.campaign),
                });
                this.sendAnalyticsEvent(AnalyticsAction.EmailSubmitted, this.campaign.id);
            } else if (this.showField === "terms") {
                this.trackEvent('creationDetailsTermsComplete', {
                    ...this.$analytics.campaignParams(this.campaign),
                });
                this.sendAnalyticsEvent(AnalyticsAction.TermsConditionsAndPrivacyPolicyAccepted, this.campaign.id);
                this.termsStepComplete = true;
            }

            this.updatedAgentMessage()
        },
        afterTransitionEntered() {
            if (this.isPreview) {
                return;
            }

            nextTick(() => {
                const emailField = (this.$refs["email"] as HTMLInputElement)
                const nameField = (this.$refs["name"] as HTMLInputElement)

                if (emailField) {
                    emailField.focus();
                }

                if (nameField) {
                    nameField.focus();
                }
            });
        },
        updatedAgentMessageForce() {
            this.agentMessage = "";
            setTimeout(() => {
                this.updatedAgentMessage();
            }, 1);
        },
        updatedAgentMessage() {
            if (this.showField === "finished") {
                this.$emit("finished", {
                    name: this.name,
                    email: this.email,
                    termsConfirmed: this.termsConfirmed
                } as OnDetailsCollectedPayload)
            }
        }
    },
});
